import React from 'react';

import { Grid, Section, Alert } from '@gogame/components';
import { FormField } from '@gogame/form';
import { dateFNS } from '@gogame/intl-ts';

import {
  AFFILIATE,
  AMOUNT,
  APPLICATION,
  CAMPAIGNS,
  COUNTRY,
  CURRENCY,
  NETWORK,
  PERIOD,
  PLATFORM,
  DEPENDENCIES,
  ADJUSTMENT_REASON,
  TYPE,
} from 'scenes/SpendAdjustments/components/SpendAdjustmentForm/constants';
import {
  campaignsRequiredSchema,
  spendAdjustmentsSchema,
} from 'scenes/SpendAdjustments/components/SpendAdjustmentForm/schemas';
import {
  loadAffiliates,
  loadApplications,
  loadCampaigns,
  loadCountries,
  loadCurrencies,
  loadNetworks,
  loadPlatforms,
  loadReasons,
  loadTypes,
} from 'scenes/SpendAdjustments/sources/fields';

const TargetingFormField = ({
  isEditing,
  values,
  getAffiliate,
  getApplications,
  getReasons,
  isDisabledAdjustReason = false,
  isCampaignField = true,
  getSpendType,
  maxDate,
  periodHelper,
  isCountryRequired = false,
  isPlatformRequired = false,
  periodMaxRange = 1,
  periodUnit = 'currentMonth',
}) => {
  const isPeriodMore4Month =
    values[PERIOD]?.[0] &&
    dateFNS.differenceInMonths(new Date(), values[PERIOD]?.[0]) >= 4;

  return (
    <React.Fragment>
      {isPeriodMore4Month ? (
        <Alert severity={'warning'}>
          The selected period is older than 4 months. Spend will be applied and
          appear in Advertising Tracker within a few days after Spend Adjustment
          is approved.
        </Alert>
      ) : (
        <Alert severity={'info'}>
          Spend will be applied and appear in Advertising Tracker within 6 hours
          after Spend Adjustment is approved
        </Alert>
      )}
      <Section title={'Period'}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <FormField.DateRangePicker
              required={true}
              label={'Period'}
              name={PERIOD}
              maxRange={periodMaxRange}
              unit={periodUnit}
              maxDate={maxDate || dateFNS.subDays(new Date(), 1)}
              dense={true}
              presets={[]}
              placeholder={''}
              disabled={isEditing}
              padding={0}
              helper={periodHelper}
            />
          </Grid>
        </Grid>
      </Section>
      <Section title={'Target'}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <FormField.Select
              lazy={true}
              required={true}
              label={'Affiliate'}
              name={AFFILIATE}
              depends={DEPENDENCIES[AFFILIATE]}
              source={getAffiliate ? getAffiliate : loadAffiliates}
              placeholder={'Select affiliate'}
              disabled={isEditing}
              padding={0}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField.Select
              lazy={true}
              required={true}
              label={'Application'}
              name={APPLICATION}
              depends={DEPENDENCIES[APPLICATION]}
              source={getApplications ? getApplications : loadApplications}
              placeholder={'Select application'}
              disabled={isEditing}
              padding={0}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField.Select
              required={true}
              label={'Network'}
              lazy={true}
              name={NETWORK}
              depends={DEPENDENCIES[NETWORK]}
              source={loadNetworks}
              placeholder={'Select network'}
              disabled={isEditing}
              padding={0}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField.Select
              label={'Platform'}
              lazy={true}
              name={PLATFORM}
              depends={DEPENDENCIES[PLATFORM]}
              source={loadPlatforms}
              placeholder={'Select platform'}
              disabled={isEditing}
              required={isPlatformRequired}
              padding={0}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField.Select
              label={'Countries'}
              lazy={true}
              name={COUNTRY}
              depends={DEPENDENCIES[COUNTRY]}
              source={loadCountries}
              placeholder={'Select country'}
              disabled={isEditing}
              required={isCountryRequired}
              padding={0}
            />
          </Grid>
          {isCampaignField && (
            <Grid item xs={4}>
              <FormField.MultiSelect
                label={'Campaigns'}
                name={CAMPAIGNS}
                depends={DEPENDENCIES[CAMPAIGNS]}
                source={loadCampaigns}
                lazy={true}
                active={campaignsRequiredSchema.isValidSync(values)}
                disabled={isEditing}
                placeholder={'All'}
                withAll={true}
                padding={0}
              />
            </Grid>
          )}
        </Grid>
      </Section>
      <Section title={'Amount'}>
        <Grid container spacing={3}>
          <Grid item xs={2}>
            <FormField.TextField
              label={'Amount'}
              name={AMOUNT}
              type={'currency'}
              currency={values.currency}
              variant={'outlined'}
              placeholder={'0'}
              debounceTimeout={800}
              padding={0}
              required={true}
            />
          </Grid>
          <Grid item xs={2}>
            <FormField.Select
              label={'Currency'}
              name={CURRENCY}
              source={loadCurrencies}
              placeholder={'Select currency'}
              disabled={isEditing}
              padding={0}
              required={true}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField.Select
              required={true}
              label={'Spend type'}
              name={TYPE}
              depends={DEPENDENCIES[TYPE]}
              active={spendAdjustmentsSchema.isValidSync(values)}
              source={getSpendType ? getSpendType : loadTypes}
              placeholder={'Select type'}
              disabled={getSpendType || isEditing}
              padding={0}
            />
          </Grid>
          <Grid item xs={4}>
            <FormField.Select
              required={true}
              label={'Adjustment reason'}
              name={ADJUSTMENT_REASON}
              source={getReasons ? getReasons : loadReasons}
              disabled={isDisabledAdjustReason}
              placeholder={'Select adjustment reason'}
              padding={0}
            />
          </Grid>
        </Grid>
      </Section>
    </React.Fragment>
  );
};

export default TargetingFormField;
